/**
 * Utility functions for calculating distance using Google Maps API
 */

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

export interface DistanceResult {
  distanceKm: number;
  durationMinutes: number;
  distanceText: string;
  durationText: string;
}

export interface DistanceMatrixResult {
  distance: google.maps.Distance;
  duration: google.maps.Duration;
  status: google.maps.DistanceMatrixElementStatus;
}

/**
 * Calculate distance and duration between two locations using haversine formula
 * @param origin - Origin coordinates {lat, lng}
 * @param destination - Destination coordinates {lat, lng}
 * @returns Distance and duration information
 */
export async function calculateDistance(
  origin: { lat: number; lng: number },
  destination: { lat: number; lng: number }
): Promise<DistanceResult> {
  try {
    // Use haversine formula to calculate distance
    const distanceKm = calculateStraightLineDistance(
      origin.lat,
      origin.lng,
      destination.lat,
      destination.lng
    );

    // Estimate duration: average speed ~40 km/h in urban areas
    const durationMinutes = Math.ceil((distanceKm / 40) * 60);

    return {
      distanceKm,
      durationMinutes,
      distanceText: `${distanceKm} km`,
      durationText: `${durationMinutes} mins`,
    };
  } catch (error) {
    console.error('Error calculating distance:', error);
    throw error;
  }
}

/**
 * Calculate distance using Google Maps Distance Matrix API
 * @param origin - Origin address or coordinates
 * @param destination - Destination address or coordinates
 * @returns Distance and duration information
 */
export async function calculateDistanceWithGoogle(
  origin: string | { lat: number; lng: number },
  destination: string | { lat: number; lng: number }
): Promise<DistanceResult> {
  if (!GOOGLE_MAPS_API_KEY) {
    throw new Error('Unable to process distance calculation at the moment. Please try after some time.');
  }

  try {
    // Format origin and destination
    const originStr = typeof origin === 'string' 
      ? encodeURIComponent(origin)
      : `${origin.lat},${origin.lng}`;
    
    const destinationStr = typeof destination === 'string'
      ? encodeURIComponent(destination)
      : `${destination.lat},${destination.lng}`;

    // Call Google Maps Distance Matrix API
    const url = `https://maps.googleapis.com/maps/api/distancematrix/json?origins=${originStr}&destinations=${destinationStr}&mode=driving&key=${GOOGLE_MAPS_API_KEY}`;
    
    const response = await fetch(url);
    const data = await response.json();

    if (data.status !== 'OK') {
      throw new Error(`Unable to calculate distance: ${data.status}`);
    }

    const element = data.rows[0]?.elements[0];
    
    if (!element || element.status !== 'OK') {
      throw new Error('Unable to calculate distance');
    }

    // Extract distance and duration
    const distanceMeters = element.distance.value;
    const durationSeconds = element.duration.value;

    return {
      distanceKm: Math.round((distanceMeters / 1000) * 10) / 10, // Round to 1 decimal
      durationMinutes: Math.ceil(durationSeconds / 60),
      distanceText: element.distance.text,
      durationText: element.duration.text,
    };
  } catch (error) {
    console.error('Error calculating distance with Google:', error);
    throw error;
  }
}

/**
 * Calculate distance using browser's Geolocation API (straight-line distance)
 * This is a fallback method and less accurate than Google Maps routing
 */
export function calculateStraightLineDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371; // Radius of the Earth in km
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;
  
  return Math.round(distance * 10) / 10; // Round to 1 decimal
}

function toRad(degrees: number): number {
  return degrees * (Math.PI / 180);
}


type Coordinates = {
  latitude: number;
  longitude: number;
};

export async function getDrivingDistance(
  pickup: Coordinates,
  dropoff: Coordinates
) {
  const url = `https://maps.googleapis.com/maps/api/distancematrix/json?origins=${pickup.latitude},${pickup.longitude}&destinations=${dropoff.latitude},${dropoff.longitude}&mode=driving&units=metric&key=${GOOGLE_MAPS_API_KEY}`;

  try {
    const response = await fetch(url);
    const data = await response.json();

    if (
      data.rows &&
      data.rows[0].elements &&
      data.rows[0].elements[0].status === "OK"
    ) {
      return {
        distanceText: data.rows[0].elements[0].distance.text, // "347 km"
        distanceValue: data.rows[0].elements[0].distance.value, // meters
        durationText: data.rows[0].elements[0].duration.text, // "6 hours 20 mins"
        durationValue: data.rows[0].elements[0].duration.value // seconds
      };
    } else {
      throw new Error("Distance not available");
    }
  } catch (error) {
    console.error("Distance API Error:", error);
    throw error;
  }
}

/**
 * Calculate distance using Google Maps DistanceMatrixService (JavaScript API)
 * This is the frontend-optimized approach using the JS SDK
 * @param origin - Origin coordinates {lat, lng}
 * @param destination - Destination coordinates {lat, lng}
 * @returns Promise with distance and duration information
 */
export async function calculateDistanceWithGoogleJS(
  origin: { lat: number; lng: number },
  destination: { lat: number; lng: number }
): Promise<DistanceResult> {
  return new Promise((resolve, reject) => {
    // Ensure Google Maps API is loaded
    if (typeof google === 'undefined' || !google.maps) {
      reject(new Error('Google Maps API not loaded'));
      return;
    }

    const service = new google.maps.DistanceMatrixService();

    service.getDistanceMatrix(
      {
        origins: [`${origin.lat},${origin.lng}`],
        destinations: [`${destination.lat},${destination.lng}`],
        travelMode: google.maps.TravelMode.DRIVING,
        unitSystem: google.maps.UnitSystem.METRIC,
      },
      (response, status) => {
        if (status === google.maps.DistanceMatrixStatus.OK) {
          const element = response?.rows[0]?.elements[0];

          if (element && element.status === google.maps.DistanceMatrixElementStatus.OK) {
            const distanceMeters = element.distance.value;
            const durationSeconds = element.duration.value;

            resolve({
              distanceKm: Math.round((distanceMeters / 1000) * 10) / 10,
              durationMinutes: Math.ceil(durationSeconds / 60),
              distanceText: element.distance.text,
              durationText: element.duration.text,
            });
          } else {
            reject(new Error('Unable to calculate distance between locations'));
          }
        } else {
          reject(new Error(`Distance Matrix API error: ${status}`));
        }
      }
    );
  });
}
